import {
    RiArrowDownSLine, RiCalendarScheduleLine,
    RiCheckFill, RiCloseFill,
    RiEdit2Line,
    RiEyeLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef, useState,
} from "react";
import { RiSendPlaneFill } from "react-icons/ri";
import ReactQuill from "react-quill";
import { TicketItem } from "../../../../contexts/ticket-state";

import {
    ChatStatus,
    TicketMessage,
    TicketMessageArticleInput, TicketMessageAttachment, TicketMessageType,
} from "../../../../__generated__/api";
import { ReactComponent as ScheduleIcon } from "../../../../assets/icons/schedule6.svg";
import usePortal from "../../../../contexts/PortalContext";
import { useDebounceWithManager } from "../../../../debouncer";
import useChangingCallback from "../../../../use-changing-callback";
import ArticlePreview from "../../../agent/answer-finder/ArticlePreview";
import { UploadedFile } from "../../../agent/text-entry/text-entry";
import Attachment from "../../../attachment/Attachment";
import useChatState from "../../../chat/chat-state";
import Scheduler from "../../../scheduler/Scheduler";
import Button from "../../../theme/button/Button";
import Dropdown from "../../../theme/dropdown/Dropdown";
import Toggle from "../../../theme/toggle/Toggle";
import Editor, { MentionedUser } from "../../editor/Editor";
import Styles from "./Conversation.module.scss";
import ScheduleInfo from "./ScheduleInfo";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { Events } from "../../../../contexts/ticket-state/ticket-item";
import ArtificialIntelligenceButton from "../../../artificial-intelligence/AIButton";

interface TextEntryProps {
    ticket: TicketItem;
    onRemoveAttachment: (attachments: UploadedFile[]) => void;
    onSetAttachments: (attachments: UploadedFile[]) => void;
    attachments: UploadedFile[];
    onUploadAttachments: (file: File[]) => Promise<void>;
    onAttachmentClicked: () => void;
    scheduledMessages: TicketMessage[];
    onToggleScheduledMessages: () => void;
    isReadOnly: boolean;
}

const DEFAULT_DRAFT = {
    type: TicketMessageType.AgentMessage,
    text: "",
    attachmentReferences: [],
    articles: [],
    attachments: [],
    shouldAddNewMentions: false,
};

const AI_RESPONSE_FIELD = "urn:fq:artificial-intelligence-field:next-response";
const AI_RESPONSE_ARTICLES_FIELD = "urn:fq:artificial-intelligence-field:next-response-articles";

export default function TextEntry({
    ticket, onRemoveAttachment, attachments, onUploadAttachments, onAttachmentClicked,
    onSetAttachments, scheduledMessages, onToggleScheduledMessages, isReadOnly,
}: TextEntryProps) {
    const [scheduledDate, setScheduledDate] = useState<Date>();
    const [shouldShowScheduler, setShowScheduler] = useState(false);
    const [newMentionedUsers, setNewMentioned] = useState<MentionedUser[]>();
    const [shouldAddParticipants, setShouldAddParticipants] = useState(false);
    const chatState = useChatState();

    const [storedAttachments, setStoredAttachments] = useState<TicketMessageAttachment[]>([]);

    const editorRef = React.createRef<ReactQuill>();
    const { isAIEnabled, aiFeatures } = usePortal();

    const draft = ticket.latestDraft;
    const [type, setType] = useState(draft?.type ?? TicketMessageType.AgentMessage);

    const [text, setText] = useState<string>(draft?.body ?? "");
    const latestDraftRef = useRef<{
        ticket: TicketItem;
        type: TicketMessageType;
        text: string;
        attachmentReferences: TicketMessageAttachment[];
        articles: TicketMessageArticleInput[];
        attachments: UploadedFile[];
        scheduledDate?: Date;
    }>({
        ticket,
        ...DEFAULT_DRAFT,
    });

    const [articles, setArticles] = useState<TicketMessageArticleInput[]>(draft?.articles ?? []);

    const editingMessage = useMemo(() => (
        chatState.editingMessageId ? ticket.messages.find(msg => msg.id === chatState.editingMessageId) : undefined
    ), [chatState.editingMessageId]);

    const storeDraft = useDebounceWithManager(async () => {
        await ticket.saveDraft({
            body: latestDraftRef.current.text ?? text,
            scheduledDate,
            attachments: latestDraftRef.current.attachments,
            articles: latestDraftRef.current.articles,
            attachmentReferences: latestDraftRef.current.attachmentReferences ?? [],
            type: latestDraftRef.current.type,
        });

        if (attachments) {
            const { latestDraft } = ticket;

            onSetAttachments(latestDraft?.attachments ?? []);
            setStoredAttachments(latestDraft?.attachmentReferences ?? []);
        }
    }, 2000, [ticket, latestDraftRef]);

    const restoreText = useChangingCallback(() => {
        const body = latestDraftRef.current.text ?? ticket.latestDraft?.body ?? "";
        setText(body);
        // editorRef.current.setEditorContents(editor, body);
        setArticles(latestDraftRef.current?.articles ?? []);
        onSetAttachments(latestDraftRef.current?.attachments ?? []);
        setStoredAttachments(latestDraftRef.current?.attachmentReferences as TicketMessageAttachment[]);

        if (editorRef.current?.editor) {
            editorRef.current?.setEditorContents(editorRef.current.editor, body);
        }

        LoggingHandler.instance.nextTick(() => {
            if (!editorRef.current?.editor) {
                return;
            }
            const { selectionStart, selectionLength } = ticket.selection || {};

            editorRef.current?.focus();
            editorRef.current?.editor?.setSelection(
                selectionStart || editorRef.current?.editor?.getLength() || 0,
                selectionLength || 0,
                "api",
            );
        });
    }, [ticket, editorRef]);

    const draftNumber = ticket.useEvent(Events.DRAFT);

    useEffect(() => {
        if (!editingMessage) {
            const { latestDraft } = ticket;
            const newText = latestDraft?.body ?? "";
            latestDraftRef.current = {
                ...latestDraftRef.current,
                text: newText,
                ticket,
            };

            restoreText();
        }
    }, [draftNumber, ticket]);

    const ticketUpdatedNumber = ticket.useEvent(Events.LISTENERS);

    const aiResponse = useMemo(
        () => (aiFeatures.isRecommendResponseEnabled !== false
            ? ticket.aiValues?.find(field => field.id === AI_RESPONSE_FIELD)?.value?.string
            : undefined),
        [ticketUpdatedNumber, aiFeatures],
    );

    useEffect(() => {
        if (editingMessage) {
            return undefined;
        }

        storeDraft.fireNow();
        const latestTicket = ticket;
        const { latestDraft } = ticket;
        const newText = latestDraft?.body ?? "";
        latestDraftRef.current = {
            ...DEFAULT_DRAFT,
            text: newText,
            ticket,
        };

        restoreText();

        return () => {
            latestTicket.saveDraft({
                body: latestDraftRef.current?.text ?? text,
                scheduledDate,
                attachments: latestDraftRef.current.attachments,
                articles: latestDraftRef.current.articles,
                attachmentReferences: latestDraftRef.current.attachmentReferences ?? [],
                type: latestDraftRef.current.type,
            });
        };
    }, [editingMessage, ticket]);

    useEffect(() => {
        const editor = editorRef.current;
        const lastTicket = ticket;
        return () => {
            const currentSelection = editor?.getEditorSelection();
            lastTicket.saveSelection({
                selectionLength: currentSelection?.length ?? 0,
                selectionStart: currentSelection?.index ?? 0,
            });
        };
    }, [ticket, editorRef.current]);

    const pressed = async () => {
        const isPublic = latestDraftRef.current.type === TicketMessageType.AgentMessage
            || latestDraftRef.current.type === TicketMessageType.UserMessage;

        const html = latestDraftRef.current.text;
        if ((!html || html.trim() === "<p></p>") && articles.isEmpty && attachments.isEmpty && storedAttachments.isEmpty) {
            return;
        }

        const status = latestDraftRef.current?.ticket.status;
        if (!isReadOnly && (status === ChatStatus.ChatRequested || status === ChatStatus.Connected)) {
            latestDraftRef.current.ticket.assign({
                shouldAssignSelf: true,
            });
        }

        if (shouldAddParticipants && newMentionedUsers?.isNotEmpty) {
            latestDraftRef.current.ticket.addParticipants(newMentionedUsers.toIdList());
        }

        setScheduledDate(undefined);
        setText("");
        onSetAttachments([]);
        setStoredAttachments([]);
        setNewMentioned(undefined);
        setShouldAddParticipants(false);
        editorRef.current?.focus();
        setArticles([]);

        LoggingHandler.instance.metric([LoggingHandler.Metrics.AGENT, "text-entry", isPublic ? "sent-message" : "sent-note"]);
        (latestDraftRef.current.ticket ?? ticket).sendMessage({
            type: latestDraftRef.current.type,
            body: html,
            scheduledDate: latestDraftRef.current.scheduledDate,
            attachmentTokens: latestDraftRef.current?.attachments.toIdList("token").filterNull(),
            articles: latestDraftRef.current?.articles,
            attachmentReferences: latestDraftRef.current?.attachmentReferences ?? [],
        });

        latestDraftRef.current = {
            ticket: latestDraftRef.current.ticket,
            ...DEFAULT_DRAFT,
        };

        if (!isPublic) {
            setType(TicketMessageType.AgentMessage);
        }
    };

    const changeHandler = useCallback((html: string, _clean: string, mentioned?: MentionedUser[]) => {
        if (html !== latestDraftRef.current?.text) {
            setText(html);

            if (!editingMessage) {
                latestDraftRef.current ??= { ...DEFAULT_DRAFT, ticket };
                latestDraftRef.current.text = html;
                storeDraft.bounce();
            }
        }

        if (!editingMessage) {
            const ids = new Set(ticket.allUsers?.toIdList() || []);
            const newMentions = mentioned?.filter(user => !ids.has(user.id));
            if (newMentions?.isEmpty) {
                setShouldAddParticipants(false);
            }

            setNewMentioned(newMentions);
        }
    }, [storeDraft, ticket, editingMessage]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        if (editingMessage) {
            const currentSelection = editor?.getEditorSelection();
            ticket.saveSelection({
                selectionLength: currentSelection?.length ?? 0,
                selectionStart: currentSelection?.index ?? 0,
            });

            setArticles([]);
            onSetAttachments([]);
            setStoredAttachments([]);
            setText(editingMessage.body.html ?? "");
        }
    }, [editingMessage]);

    useEffect(() => {
        if (editingMessage) {
            return;
        }

        latestDraftRef.current = {
            type,
            text,
            attachments,
            attachmentReferences: storedAttachments,
            articles,
            scheduledDate,
            ticket: latestDraftRef.current.ticket ?? ticket,
        };
    }, [text, attachments, storedAttachments, articles, type, scheduledDate]);

    useEffect(() => {
        if (attachments.isNotEmpty) {
            storeDraft.bounce();
        }
    }, [attachments]);

    const newMentionsMessage = useMemo(() => {
        if (!newMentionedUsers || newMentionedUsers.isEmpty) {
            return "";
        }

        const names = newMentionedUsers?.toIdList("name");
        if (names.length > 1) {
            names.push(`and ${names.pop()}`);
        }

        const suffix = names.length > 1 ? "participants" : "a participant";
        return `Add ${names.join(names.length > 2 ? ", " : " ")} as ${suffix}.`;
    }, [newMentionedUsers]);

    const saveOnEdit = async () => {
        if (editingMessage) {
            await ticket.editMessage(editingMessage.id, text);
            chatState.setEditingMessageId(undefined);
            restoreText();
        }
    };

    const sendButton = useMemo(() => {
        if (editingMessage) {
            return (
                <>
                    <Button
                        size="small"
                        variant="primary"
                        onClick={saveOnEdit}
                        className={Styles["save-edit"]}
                        prefixIcon={<RiCheckFill />}
                    />
                    <Button
                        size="small"
                        variant="outline-gray"
                        onClick={() => chatState.setEditingMessageId(undefined)}
                        prefixIcon={<RiCloseFill />}
                    />
                </>
            );
        }
        if (type === TicketMessageType.AgentMessage) {
            return (
                <div className="btn-group">
                    <Button
                        size="small"
                        variant="primary"
                        prefixIcon={scheduledDate ? <ScheduleIcon /> : <RiSendPlaneFill />}
                        title={scheduledDate ? "Send Scheduled Message" : "Send message"}
                        onClick={pressed as (() => void)}
                    />

                    {
                        !isReadOnly && (
                            <Dropdown placement="top-end">
                                <Dropdown.Toggle
                                    size="small"
                                    variant="primary"
                                    split="left"
                                    prefixIcon={<RiArrowDownSLine />}
                                />
                                <Dropdown.Menu flip>
                                    <Dropdown.Item
                                        iconSize="xsmall"
                                        prefixIcon={<RiCalendarScheduleLine />}
                                        onClick={() => setShowScheduler(true)}
                                        metricPath={[LoggingHandler.Metrics.AGENT, "chat", "editor", "schedule-message-start"]}

                                    >
                                        Schedule a Reply
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )
                    }
                </div>
            );
        }
        return (
            <>
                <div className={Styles["add-note-comment"]}>
                    <RiEyeLine />
                    Visible only to agents.
                </div>
                <Button
                    size="small"
                    variant="primary"
                    onClick={pressed as () => void}
                >
                    Add Note
                </Button>
            </>
        );
    }, [editingMessage, type, saveOnEdit, chatState, pressed, scheduledDate]);

    return (
        <>
            <ScheduleInfo
                onCancel={() => setScheduledDate(undefined)}
                newMessageDate={scheduledDate}
                scheduledMessages={scheduledMessages}
                onSwitchView={onToggleScheduledMessages}
            />

            {newMentionsMessage && !editingMessage && (
                <div
                    className={classNames(
                        Styles["new-mentions"],
                        Styles[`new-mentions-${type.toLowerCase().replace(/_/g, "-")}`],
                    )}
                >
                    <Toggle
                        checked={shouldAddParticipants}
                        onChange={() => setShouldAddParticipants(!shouldAddParticipants)}
                        text={newMentionsMessage}
                        size="small"
                    />
                </div>
            )}

            {editingMessage && (
                <div className={Styles["edit-header"]}>
                    <div className={Styles["edit-header-message"]}>
                        <RiEdit2Line />
                        {" "}
                        Edit Message:
                    </div>
                    <div title={editingMessage.body.plaintext ?? ""} className={Styles["edit-preview"]}>
                        {editingMessage.body.plaintext}
                    </div>
                </div>
            )}

            <div
                data-tour-panel="chat-text-entry"
                className={classNames(
                    Styles["text-entry"],
                    Styles[`text-entry-${type.toLowerCase().replace(/_/g, "-")}`],
                )}
            >

                {
                    !isReadOnly && (
                        <Scheduler
                            date={scheduledDate}
                            shouldShow={shouldShowScheduler}
                            onCancel={() => setShowScheduler(false)}
                            title="Schedule a Reply"
                            onSelected={date => {
                                setShowScheduler(false);
                                setScheduledDate(date);
                                latestDraftRef.current.scheduledDate = date;
                            }}
                        />
                    )
                }

                <Editor
                    onSend={editingMessage ? saveOnEdit : pressed}
                    onTypeChange={editingMessage || isReadOnly ? undefined : setType}
                    type={type}
                    value={text}
                    ref={editorRef}
                    metricPathPrefix={[LoggingHandler.Metrics.AGENT, "chat", "editor"]}
                    mentionSourceId={ticket.chat.source?.id}
                    onChange={changeHandler}
                    isAllowAI={!isReadOnly && isAIEnabled && ticket.ticket.artificialIntelligence?.isDisabled === false}
                    onAttachmentClicked={onAttachmentClicked}
                    onUploadAttachments={onUploadAttachments}
                    onArticleSelected={!isReadOnly && (latestDraftRef.current.type === TicketMessageType.AgentMessage
                        || latestDraftRef.current.type === TicketMessageType.UserMessage) ? article => {
                            setArticles(prev => [...prev, article]);
                            storeDraft.bounce();
                        } : undefined}
                    bottomEditorPanel={(
                        <>
                            {aiResponse && (
                                <div className={Styles["ai-button-container"]}>
                                    <ArtificialIntelligenceButton
                                        metricPath={[LoggingHandler.Metrics.AGENT, "chat", "editor", "ai-auto-response"]}
                                        onClick={() => {
                                            const aiArticles = ticket.aiValues?.find(
                                                field => field.id === AI_RESPONSE_ARTICLES_FIELD,
                                            )?.value?.string;

                                            // eslint-disable-next-line no-param-reassign
                                            ticket.aiValues = ticket.aiValues?.filter(
                                                row => row.id !== AI_RESPONSE_FIELD && row.id !== AI_RESPONSE_ARTICLES_FIELD,
                                            );
                                            changeHandler(aiResponse, aiResponse, []);

                                            if (aiArticles) {
                                                const parsedArticles = JSON.parse(aiArticles);
                                                setArticles(parsedArticles);
                                            }
                                        }}
                                        name="AI Response"
                                        previewText={aiResponse}
                                    />
                                </div>
                            )}
                            <div className={Styles["attachments-panel"]}>

                                {storedAttachments?.map(attach => (
                                    <Attachment
                                        size="sm"
                                        url={attach.url as string}
                                        mimeType={attach.mimeType as string}
                                        title={attach.title as string}
                                        key={attach.url}
                                        onClose={() => setStoredAttachments(
                                            storedAttachments.filter(row => row.url !== attach.url),
                                        )}
                                        variant="uploaded"
                                    />
                                ))}
                                {attachments?.map(attach => (
                                    <Attachment
                                        size="sm"
                                        url={attach.url as string}
                                        mimeType={attach.mimeType}
                                        title={attach.name}
                                        key={attach.url}
                                        isUploading={attach.isUploading}
                                        onClose={() => onRemoveAttachment?.(attachments.filter(row => row.url !== attach.url))}
                                        variant="uploaded"
                                    />
                                ))}
                                {articles?.map(article => (
                                    <ArticlePreview
                                        variant="preview"
                                        className="col-4"
                                        article={article}
                                        onClose={() => setArticles?.(articles.filter(row => row.id !== article.id))}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    sendButton={sendButton}
                />
            </div>
        </>
    );
}
TextEntry.defaultProps = {};
